import React, { ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

// Minimal v3 modal that renders into the existing `#modal-root` portal.
// Wraps children in `data-v3` so v3 tokens cascade inside.
//
// Closes on Escape and on backdrop click.  Open/close is fully controlled by
// the parent — no internal `open` state.

interface Props {
  title: string;
  onClose: () => void;
  footer?: ReactNode;
  children: ReactNode;
  /** Wider variant for forms with many fields (max-width 640px). */
  wide?: boolean;
}

const Modal = ({ title, onClose, footer, children, wide }: Props) => {
  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const portalRoot = typeof document !== 'undefined' ? document.getElementById('modal-root') : null;
  if (!portalRoot) return null;

  return createPortal(
    <div
      data-v3="true"
      data-v3-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`v3-modal${wide ? ' wide' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="v3-modal-h">
          <h3>{title}</h3>
          <button type="button" className="close-x" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="v3-modal-body">{children}</div>
        {footer && <div className="v3-modal-foot">{footer}</div>}
      </div>
    </div>,
    portalRoot
  );
};

export default Modal;
